import * as React from 'react';

import {
  DescriptionPage_MarketingSource as MarketingSource,
  DescriptionPage_ProductVariant as ProductVariant,
} from '__generated__/graphql-types';

import { useLocation, useParams } from 'js/lib/useRouter';

import { redirectClientOrServer } from 'bundles/common/utils/urlUtils';
import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus-common/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import useGenerateGooglePriceTestImpression from 'bundles/payments/utils/useGenerateGooglePriceTestImpression';
import getProductTitle from 'bundles/unified-common/utils/getProductTitle';
import AdminBannerNotConfigurable from 'bundles/unified-description-page-common/components/admin/AdminBannerNotConfigurable';
import ConsumerSpecializationEnrollButtonNotConfigurable from 'bundles/unified-description-page-common/components/enroll/consumer/ConsumerSpecializationEnrollButtonNotConfigurable';
import MobileCtaModule from 'bundles/unified-description-page-common/components/modules/mobile-cta/MobileCtaModule';
import FixedPageNavigation from 'bundles/unified-description-page-common/components/modules/page-navigation/FixedPageNavigation';
import NavPartnerLogo from 'bundles/unified-description-page-common/components/modules/page-navigation/NavPartnerLogo';
import SpecializationAndProfessionalCertificatePageSEO from 'bundles/unified-description-page-common/components/seo/SpecializationAndProfessionalCertificatePageSEO';
import CoBrandingPartnerLogos from 'bundles/unified-description-page-common/components/shared/CoBrandingPartnerLogos';
import PageDataErrorHandler from 'bundles/unified-description-page-common/components/shared/PageDataErrorHandler';
import { GOOGLE_XDP_SLUG_TO_PROF_CERT } from 'bundles/unified-description-page-common/constants/googleCerts';
import { useSpecializationPageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffSpecializationContext';
import useLogRecentlyViewed from 'bundles/unified-description-page-common/hooks/useRecentlyViewed';
import ConsumerSpecializationCitationModuleRenderer from 'bundles/unified-description-page-common/module-renderers/citation/ConsumerSpecializationCitationModuleRenderer';
import Renderer from 'bundles/unified-description-page-common/pages/consumer/specialization/ConsumerSpecializationPaidMediaPageRenderer';
import EventingDataProvider from 'bundles/unified-description-page-common/providers/EventingDataProvider';
import LanguageInfoDialogProvider from 'bundles/unified-description-page-common/providers/LanguageInfoDialogProvider';
import PageInfoProvider from 'bundles/unified-description-page-common/providers/PageInfoProvider';
import PageNavigationProvider from 'bundles/unified-description-page-common/providers/PageNavigationProvider';
import StickyPanelProvider from 'bundles/unified-description-page-common/providers/StickyPanelProvider';
import TranslationsNotificationBannerProvider from 'bundles/unified-description-page-common/providers/consumer/TranslationsNotificationBannerProvider';
import { ModuleNames } from 'bundles/unified-description-page-common/types/moduleConfig';
import {
  SPECIALIZATION_NAV_ITEMS,
  getSpecializationPageNavigationLinks,
} from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';
import getPartnersForSpecialization from 'bundles/unified-description-page-common/utils/getPartnersForSpecialization';
import getProductId from 'bundles/unified-description-page-common/utils/getProductId';
import { getConsumerRouteForSpecializationProducts } from 'bundles/unified-description-page-common/utils/getRouteForSpecializationProducts';
import getVariantTitle from 'bundles/unified-description-page-common/utils/getVariantTitle';
import hasCareerOutcomesModule from 'bundles/unified-description-page-common/utils/hasCareerOutcomesModule';
import isSlugDuplicatedGoogleCert from 'bundles/unified-description-page-common/utils/isSlugDuplicatedGoogleCert';

const ConsumerSpecializationPaidMediaPageRootContent = () => {
  const pageData = useSpecializationPageData();
  const { pathname, search } = useLocation();
  const productSlugInUrl = useParams().productSlug;

  useLogRecentlyViewed({
    name: pageData?.name,
    slug: pageData?.slug,
    partners: pageData?.partners,
    id: getProductId('specialization', pageData?.id),
    difficulty: pageData?.difficultyLevel,
    productType: pageData?.productVariant,
    careerGrowthInPercentage: pageData?.careerOutcomes?.careerGrowthInPercentage,
    medianEntryLevelSalary: pageData?.careerOutcomes?.medianEntryLevelSalary,
    jobOpeningsInUs: pageData?.careerOutcomes?.jobOpeningsInUs,
  });

  useGenerateGooglePriceTestImpression({
    hasSpecialization: true,
    partners: pageData?.partners,
  });

  useGenerateStartFreeTrialTestEpicImpression({});

  if (!pageData) {
    return null;
  } else if (isSlugDuplicatedGoogleCert(pageData.slug)) {
    // If duplicated Google Cert slug, redirect to canonical source.
    redirectClientOrServer(
      getConsumerRouteForSpecializationProducts(
        ProductVariant.ProfessionalCertificate,
        GOOGLE_XDP_SLUG_TO_PROF_CERT[pageData.slug as keyof typeof GOOGLE_XDP_SLUG_TO_PROF_CERT]
      )
    );
  } else if (pageData.productVariant === ProductVariant.GoogleCertificate) {
    return <PageDataErrorHandler pathname={pathname} />;
  } else if (pageData.productVariant !== ProductVariant.StandardSpecialization || productSlugInUrl !== pageData.slug) {
    redirectClientOrServer(getConsumerRouteForSpecializationProducts(pageData.productVariant, pageData.slug, search));
  }

  const partnersToDisplay = getPartnersForSpecialization(pageData.partners, !!pageData.coBrandingEnabled);
  const productTitle = getProductTitle(pageData.name, pageData.productVariant);

  const pageConfig = {
    modules: [
      { name: ModuleNames.PageBanner },
      {
        name: ModuleNames.ProductOverview,
      },
      {
        name: ModuleNames.CareerOutcomes,
      },
      {
        name: ModuleNames.LearnerIncentive,
      },
      {
        name: ModuleNames.Syllabus,
      },
      {
        name: ModuleNames.Testimonials,
      },
      {
        name: ModuleNames.Faqs,
      },
    ],
  };

  const navigationLinks = getSpecializationPageNavigationLinks({
    pageData,
    pageConfig,
  });

  return (
    <PageInfoProvider marketingSource={MarketingSource.PaidMedia} navigationIdMap={SPECIALIZATION_NAV_ITEMS}>
      <EventingDataProvider productId={pageData.id} productType="s12n">
        <PageNavigationProvider navigationLinks={navigationLinks}>
          <StickyPanelProvider>
            <LanguageInfoDialogProvider
              languages={{
                primaryLanguages: pageData.primaryLanguages,
                subtitleLanguages: pageData.subtitleLanguages ?? [],
                translatedLanguages: pageData.translatedLanguages ?? [],
              }}
            >
              <TranslationsNotificationBannerProvider
                translatedLanguages={pageData.translatedLanguages}
                productType={getVariantTitle(pageData.productVariant)}
                // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                originalLanguage={pageData.primaryLanguages[0]}
              >
                <AdminBannerNotConfigurable slug={pageData.slug} />
                <FixedPageNavigation
                  productTitle={productTitle}
                  navigationLinks={navigationLinks}
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                  partnerLogo={
                    partnersToDisplay && partnersToDisplay.length > 1 ? (
                      <CoBrandingPartnerLogos partners={partnersToDisplay} isNavPlacement={true} />
                    ) : (
                      <NavPartnerLogo
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        imageSrc={partnersToDisplay?.[0].squareLogo}
                        // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
                        partnerName={partnersToDisplay?.[0].name}
                      />
                    )
                  }
                />
                <Renderer pageData={pageData} pageConfig={pageConfig} />
                {hasCareerOutcomesModule(pageConfig) && (
                  <ConsumerSpecializationCitationModuleRenderer pageData={pageData} />
                )}
                <MobileCtaModule
                  pageCta={
                    <ConsumerSpecializationEnrollButtonNotConfigurable
                      pageData={pageData}
                      size="small"
                      onlyShowButton
                    />
                  }
                />
                <SpecializationAndProfessionalCertificatePageSEO pageData={pageData} disableCrawlerIndexing />
              </TranslationsNotificationBannerProvider>
            </LanguageInfoDialogProvider>
          </StickyPanelProvider>
        </PageNavigationProvider>
      </EventingDataProvider>
    </PageInfoProvider>
  );
};

export default ConsumerSpecializationPaidMediaPageRootContent;
